import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogFooter } from '@you/ui'
import { Button } from '@you/ui'
import { Icon } from '@/components/Icon'
import type {
  KnowledgeBase,
  KnowledgeDocType,
} from '@/api/types/knowledge.types'
import { useKnowledge } from '@/api/hooks/useKnowledge'
import { useWorkspaceStore } from '@/stores/workspaceStore'

interface KnowledgeBaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedDocType: string
  onConfirm: (selectedKnowledgeBases: KnowledgeBase[]) => void
  existingKnowledgeBases?: KnowledgeBase[]
}

const KnowledgeBaseDialog: React.FC<KnowledgeBaseDialogProps> = ({
  open,
  onOpenChange,
  selectedDocType,
  onConfirm,
  existingKnowledgeBases = [],
}) => {
  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState<
    KnowledgeBase[]
  >([])
  const { currentWorkspace } = useWorkspaceStore()

  // 获取知识库列表
  const { knowledgeList, isLoading } = useKnowledge({
    workspaceId: currentWorkspace?.id || '',
  })

  // 根据选中的文档类型过滤知识库
  const filteredKnowledgeBases = knowledgeList.filter((knowledge) => {
    if (selectedDocType === 'text') {
      return knowledge.docType === 1 // TEXT
    } else if (selectedDocType === 'table') {
      return knowledge.docType === 2 // TABLE
    }
    return true
  })

  // 处理知识库选择
  const handleKnowledgeToggle = (knowledge: KnowledgeBase) => {
    setSelectedKnowledgeBases((prev) => {
      const isSelected = prev.some((item) => item.id === knowledge.id)
      if (isSelected) {
        return prev.filter((item) => item.id !== knowledge.id)
      } else {
        return [...prev, knowledge]
      }
    })
  }

  // 处理确认
  const handleConfirm = () => {
    onConfirm(selectedKnowledgeBases)
    onOpenChange(false)
    setSelectedKnowledgeBases([]) // 重置选择
  }

  // 处理取消
  const handleCancel = () => {
    onOpenChange(false)
    setSelectedKnowledgeBases([]) // 重置选择
  }

  // 获取文档类型图标
  const getDocTypeIcon = (docType: KnowledgeDocType) => {
    return docType === 1 ? 'text' : 'table'
  }

  // 获取文档类型名称
  const getDocTypeName = (docType: KnowledgeDocType) => {
    return docType === 1 ? '文本类' : '表格类'
  }

  // 格式化日期
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[1000px] max-w-none p-0">
        <div className="h-[667px] flex flex-col">
          <DialogHeader
            className="px-6 py-4 border-b flex-shrink-0"
            title="添加知识库"
          ></DialogHeader>

          <div className="flex-1 p-6 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-gray-500">加载中...</div>
              </div>
            ) : filteredKnowledgeBases.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-gray-500">暂无知识库</div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredKnowledgeBases.map((knowledge) => {
                  const isSelected = selectedKnowledgeBases.some(
                    (item) => item.id === knowledge.id
                  )
                  const isExisting = existingKnowledgeBases.some(
                    (item) => item.id === knowledge.id
                  )
                  return (
                    <div
                      key={knowledge.id}
                      className={`flex items-center justify-between w-full p-4 rounded-lg transition-colors ${
                        isExisting
                          ? 'bg-gray-100 opacity-60 cursor-not-allowed'
                          : 'bg-[#FDFCFF] hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        {/* 图标 */}
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <Icon
                            name={getDocTypeIcon(knowledge.docType)}
                            size={40}
                          />
                        </div>

                        {/* 名称和标签 */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 mb-2">
                            {knowledge.name}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {/* 类型标签 */}
                            <span
                              className="text-xs px-2 py-1 rounded"
                              style={{
                                background: 'rgba(233, 223, 255, 0.31)',
                                color: '#967AD4',
                                fontSize: '12px',
                                borderRadius: '4px',
                                padding: '4px 8px',
                              }}
                            >
                              {getDocTypeName(knowledge.docType)}
                            </span>

                            {/* 文件数量标签 */}
                            <span
                              className="text-xs px-2 py-1 rounded"
                              style={{
                                background: 'rgba(233, 223, 255, 0.31)',
                                color: '#967AD4',
                                fontSize: '12px',
                                borderRadius: '4px',
                                padding: '4px 8px',
                              }}
                            >
                              文件数量：{knowledge.filesNum}
                            </span>

                            {/* 创建人标签 */}
                            <span
                              className="text-xs px-2 py-1 rounded"
                              style={{
                                background: 'rgba(232, 232, 232, 0.31)',
                                color: '#8C8C8C',
                                fontSize: '12px',
                                borderRadius: '4px',
                                padding: '4px 8px',
                              }}
                            >
                              {knowledge.creator}
                            </span>

                            {/* 创建日期标签 */}
                            <span
                              className="text-xs px-2 py-1 rounded"
                              style={{
                                background: 'rgba(232, 232, 232, 0.31)',
                                color: '#8C8C8C',
                                fontSize: '12px',
                                borderRadius: '4px',
                                padding: '4px 8px',
                              }}
                            >
                              {formatDate(knowledge.createTime || '')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* 添加按钮 */}
                      <Button
                        variant={isSelected ? 'default' : 'outline'}
                        size="sm"
                        onClick={() =>
                          !isExisting && handleKnowledgeToggle(knowledge)
                        }
                        disabled={isExisting}
                        className="ml-4 flex-shrink-0"
                      >
                        {isExisting ? '已存在' : isSelected ? '已选择' : '选择'}
                      </Button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-10 border-t flex-shrink-0">
            <Button variant="secondary" onClick={handleCancel}>
              取消
            </Button>
            <Button variant="default" className="ml-2" onClick={handleConfirm}>
              确认
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default KnowledgeBaseDialog
